import React, { useRef, useState } from 'react';
import { Button, Spin } from 'antd';
import { EBookingLimitListingState, IBookingLimitVo } from 'types/bookingLimit';
import LimitRow, { TLimitRowMode } from './LimitRow';
import SummaryBand from './SummaryBand';
import { ConfirmSaveModal, ConfirmTurnOffModal } from './ConfirmModals';
import SectionHeader, { ownPills } from './SectionHeader';
import HistoryModal from './HistoryModal';
import AnnouncementModal, { announcementSeen, markAnnouncementSeen } from './AnnouncementModal';
import AmberCallout from './AmberCallout';
import InfoBanner from './InfoBanner';
import { useLimitEditor } from './useLimitEditor';
import { findRule, formatResetAt, isLimitIrrelevant } from './helpers';
import s from './s.module.less';

interface Props {
    data?: IBookingLimitVo;
    loading: boolean;
    refetch: () => void;
    /**
     * 当前用户是否在 group practice 里（来自 session，不是接口）。
     * 只影响文案：group 里这个数字对全员生效，改动会影响同事。
     */
    isGroupPractice: boolean;
}

const MyLimits: React.FC<Props> = ({ data, loading, refetch, isGroupPractice }) => {
    const rule = findRule(data?.rules);
    const editor = useLimitEditor(rule, refetch);
    const state = data?.listingState;
    const [historyOpen, setHistoryOpen] = useState(false);
    const capRef = useRef<HTMLInputElement>(null);
    /**
     * 上线公告只对「还没设过上限」的人弹，且每人一次。
     * 初值在 useState 的 initializer 里读 localStorage，避免每次 render 都碰存储。
     */
    const [announceSeen, setAnnounceSeen] = useState(() => announcementSeen());
    const closeAnnouncement = () => {
        markAnnouncementSeen();
        setAnnounceSeen(true);
    };

    if (loading && !data) {
        return <Spin />;
    }
    if (!data) {
        return null;
    }

    const readOnly = !data.canEdit;
    /** 一行的可编辑性只有这四种，优先级：未上架 > practice 暂停 > 只读 > 可编辑 */
    let mode: TLimitRowMode = 'edit';
    if (state === EBookingLimitListingState.NOT_LISTED) {
        mode = 'notListed';
    } else if (state === EBookingLimitListingState.PRACTICE_PAUSED) {
        mode = 'practicePaused';
    } else if (readOnly) {
        mode = 'readonly';
    }
    const locked = isLimitIrrelevant(state);
    const used = rule?.usedCount ?? 0;
    const cap = rule?.limitCount ?? 0;
    const full = !!rule?.enabled && cap > 0 && used >= cap;
    const resetAt = rule ? formatResetAt(rule.periodEndAt, data.timezone) : '';

    /**
     * 用量块逐字对齐原型 limitRow()（patient-limits.html:814-825）。三处以前漏了：
     *  1. 右半边「Up to N more can book」/「Profile hidden」—— 原来整块没有，
     *     justify-content:space-between 只有一个子元素，布局和设计相反
     *  2. 上限被调到低于已约数时说「9 booked this week (limit 5)」——
     *     原来恒定输出「X of Y used」，会出现「20 of 5 used」这种读不通的话
     *  3. 触顶时点名「调到几就能立刻恢复」，provider 不用猜
     */
    const over = used > cap;
    const remaining = Math.max(0, cap - used);
    const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
    const count = over
        ? `${used} booked this week (limit ${cap})`
        : `${used} of ${cap} booked this week`;
    const meta = full
        ? `Hidden until ${resetAt}. ${
            readOnly
                ? 'Your practice owner or an admin can reopen it now by raising the number.'
                : `Raise the number to ${used + 1} or more to reopen now.`
        }`
        : `Resets ${resetAt}`;

    const usage = (
        <>
            <div className={s.usageTop}>
                <span>{count}</span>
                <span className={s.usageLeft}>
                    {full ? 'Profile hidden' : `Up to ${remaining} more can book`}
                </span>
            </div>
            <div className={s.usageTrack}>
                <div className={s.usageFill} style={{ width: `${pct}%` }} />
            </div>
            <div className={s.usageMeta}>{meta}</div>
        </>
    );

    const intro = isGroupPractice
        ? `Limit how many new patients from the Klarity Health marketplace can book you per week. When you reach the limit, your profile stops showing in marketplace search until ${resetAt}. This limit is set for the whole practice and is separate from Pro-Listing.`
        : `Limit how many new patients from the Klarity Health marketplace can book you per week. When you reach the limit, your profile stops showing in marketplace search until ${resetAt}. This limit is separate from Pro-Listing and does not change your Pro-Listing budget or bids.`;

    return (
        <div>
            {/* pill 回答「我现在还在不在线上」，只随是否已达上限变化 */}
            <SectionHeader
                pills={ownPills(state, resetAt)}
                intro={intro}
                onViewHistory={() => setHistoryOpen(true)}
            />
            <div className={s.card}>
            <SummaryBand
                rule={rule}
                timezone={data.timezone}
                bookings={used}
                weeklySpentCents={data.weeklySpentCents}
                isPractice={false}
            />

            <div className={s.body}>
                {/* 未上架要先说，因为它是下面控件不能用的原因（原型 #notListed 放在最前）。
                    原来这句被降级成了开关旁的灰色小字 */}
                {state === EBookingLimitListingState.NOT_LISTED && (
                    <InfoBanner>
                        You are not listed on Klarity yet, so there is nothing for a limit to cap.{' '}
                        {readOnly
                            ? 'Your practice owner or an admin can set one once your listing is live.'
                            : 'You can set one once your listing is live; start your listing under Performance.'}
                    </InfoBanner>
                )}

                {/* 只读说明条：谁能改这个上限，是团队成员进来最先要问的（原型 #roStrip）。
                    原来这句被降级成了 cardSub 里的一个从句 */}
                {readOnly && !locked && (
                    <InfoBanner>
                        Only your practice owner or an admin can change this limit.
                    </InfoBanner>
                )}

                {/* 状态提示：只有被本功能暂停时才谈上限，其他暂停原因交给既有 banner */}
                {/* 文案由产品在《Patient Booking Limit 相关问题》C1 评论里逐字给出。
                    与后端 BookingLimitMessages.OVER_LIMIT 是同一句 —— 那边是点「恢复上架」
                    被拒时的 API 提示，这里是页面常驻提示。改一处要改两处。 */}
                {state === EBookingLimitListingState.PAUSED_BY_LIMIT && (
                    <AmberCallout>
                        You have configured a maximal allowed new patient booking per week. Your
                        weekly limit is reached. Raise the limit to relist or wait until the next
                        reset on the upcoming Monday.
                    </AmberCallout>
                )}
                {/* C2 评论原文。产品原句里「your configured the weekly maximal booking」多了个
                    the，这里去掉；其余逐字保留。 */}
                {state === EBookingLimitListingState.PAUSED_BY_LIMIT_HELD && (
                    <AmberCallout>
                        Your listing remains paused since you have reached your configured weekly
                        maximal booking. Your weekly limit is reached. Raise the limit to relist or
                        wait until the next reset on the upcoming Monday.
                    </AmberCallout>
                )}
                {/* C3：产品明确说「Just show "Your practice listing is paused"」，不要加解释 */}
                {state === EBookingLimitListingState.PRACTICE_PAUSED && (
                    <div className={s.noticeMuted}>
                        Your practice listing is paused
                    </div>
                )}

                <div>
                    <div className={s.cardTitle}>{readOnly ? 'Your limit' : 'Choose your limit'}</div>
                    {isGroupPractice && (
                        <div className={s.cardSub}>
                            This is your practice&rsquo;s limit. Every provider gets it separately, not
                            a shared pool.
                        </div>
                    )}
                </div>

                <LimitRow
                    saved={rule}
                    draft={editor.draft}
                    onChange={editor.setDraft}
                    mode={mode}
                    errMsg={editor.errMsg}
                    full={full}
                    capRef={capRef}
                    usage={usage}
                />

                {!readOnly && !locked && (editor.draft.enabled || editor.dirty) && (
                    <div className={s.actions}>
                        <Button onClick={editor.reset} disabled={!editor.dirty || editor.saving}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            loading={editor.saving}
                            disabled={!editor.dirty || !!editor.errMsg}
                            onClick={editor.submit}
                        >
                            Save
                        </Button>
                    </div>
                )}
            </div>

            <div className={s.foot}>
                <span className={s.footIcon}>i</span>
                <span>
                    A booking counts when it is made; cancellations do not give the spot back. Bookings
                    made at the same moment can push the count slightly past the limit, so it is an
                    &ldquo;up to&rdquo;, not an exact stop. A limit does not guarantee that many
                    bookings.
                </span>
            </div>

            <ConfirmSaveModal
                open={editor.confirmSave}
                isPractice={false}
                limitCount={editor.draft.limitCount ?? 0}
                willPause={editor.willPause}
                resetAt={resetAt}
                loading={editor.saving}
                onCancel={() => editor.setConfirmSave(false)}
                onConfirm={editor.commit}
            />
            <ConfirmTurnOffModal
                open={editor.confirmOff}
                isPractice={false}
                loading={editor.saving}
                onCancel={() => editor.setConfirmOff(false)}
                onConfirm={editor.commit}
            />
            </div>
            <HistoryModal
                open={historyOpen}
                isPractice={false}
                isGroupPractice={isGroupPractice}
                onClose={() => setHistoryOpen(false)}
            />
            {/* 没上架 / 只读的人看到它也做不了什么，所以这三种情况都不弹 */}
            <AnnouncementModal
                open={!announceSeen && !rule?.enabled && !readOnly && !locked}
                onLater={closeAnnouncement}
                onSetUp={() => {
                    closeAnnouncement();
                    // 原型 focusCap(true)：打开草稿开关、带上已保存的数字，再聚焦并全选
                    editor.setDraft({ enabled: true, limitCount: editor.draft.limitCount ?? cap });
                    setTimeout(() => {
                        capRef.current?.focus();
                        capRef.current?.select();
                    }, 0);
                }}
            />
        </div>
    );
};

export default MyLimits;
